import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const tBrand = tenant?.branding || {};
  const accent = tBrand.accent_color || "#f59e0b";
  const brandName = tBrand.brand_name || "Admin Panel";
  const logoUrl = tBrand.logo_url;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.role !== "super_admin") {
        setError("Access denied. Super admin credentials required.");
        setLoading(false);
        return;
      }
      localStorage.setItem("wa_admin_token", data.token);
      localStorage.setItem("wa_admin_user", JSON.stringify(data));
      navigate("/admin/dashboard");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0a1d] px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md border-indigo-900/30 bg-[#13112b]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="w-12 h-12 rounded-xl object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}33` }}>
                <Shield className="w-6 h-6" style={{ color: accent }} />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl text-white" data-testid="admin-login-brand">{brandName}</CardTitle>
          <CardDescription className="text-slate-400">Sign in with your super admin credentials</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 rounded-lg border border-red-500/20" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email" type="email" placeholder="superadmin@admin.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                data-testid="admin-login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password" type="password" placeholder="Password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                data-testid="admin-login-password"
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full font-medium" disabled={loading} data-testid="admin-login-submit" style={{ backgroundColor: accent, color: "#000" }}>
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
